import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaStar, FaExclamationCircle, FaUsers, FaCalendarCheck } from 'react-icons/fa';
import GradientButton from '../../components/GradientButton';
import { getUserDetails } from '../../actions/userActions';


const PaymentSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState('');
  const [userType, setUserType] = useState(null); // 'worker' | 'adult_worker' | 'employer'
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const orderId = searchParams.get('ext_order_id') || searchParams.get('orderId');
        
        // DEBUG: Sprawdź wszystkie parametry URL
        console.log('=== PaymentSuccess Debug ===');
        console.log('Full URL:', window.location.href);
        console.log('Search params:', {
          ext_order_id: searchParams.get('ext_order_id'),
          orderId: searchParams.get('orderId'),
          error: searchParams.get('error')
        });
        console.log('Extracted orderId:', orderId);
        console.log('===========================');
        
        if (!orderId) {
          // Sprawdź czy PayU zwrócił error (anulowanie bez orderId)
          const errorCode = searchParams.get('error');
          
          if (errorCode) {
            console.log('PayU returned error code:', errorCode);
            
            // PayU error codes:
            // 501 = Płatność anulowana przez użytkownika
            if (errorCode === '501') {
              setError('Płatność została anulowana.');
              setPaymentStatus('CANCELED');
              setLoading(false);
              return;
            }
            
            // Inne błędy
            setError('Wystąpił błąd podczas płatności.');
            setPaymentStatus('FAILED');
            setLoading(false);
            return;
          }
          
          // Brak order_id i brak error - sprawdź profil użytkownika
          // Nie ma order_id - sprawdź profil użytkownika
          let attempts = 0;
          const maxAttempts = 10; // 10 prób * 2 sekundy = 20 sekund
          
          const checkSubscription = async () => {
            try {
              const profileUrl = API_BASE ? `${API_BASE}/api/users/profile/` : '/api/users/profile/';
              const response = await fetch(profileUrl, {
                headers: {
                  'Authorization': `Bearer ${userInfo.access}`
                }
              });
              const profileData = await response.json();

              if (profileData.user_type) {
                setUserType(profileData.user_type);
              }
              
              if (profileData.subscription && profileData.subscription.is_active) {
                setPaymentStatus('COMPLETED');
                setLoading(false);
              } else {
                attempts++;
                if (attempts < maxAttempts) {
                  setTimeout(checkSubscription, 2000);
                } else {
                  setError('Nie udało się potwierdzić płatności. Jeśli płatność została pobrana, skontaktuj się z nami.');
                  setPaymentStatus('FAILED');
                  setLoading(false);
                }
              }
            } catch (err) {
              console.error('Błąd sprawdzania profilu:', err);
              attempts++;
              if (attempts < maxAttempts) {
                setTimeout(checkSubscription, 2000);
              } else {
                setError('Nie udało się potwierdzić płatności. Jeśli płatność została pobrana, skontaktuj się z nami.');
                setPaymentStatus('FAILED');
                setLoading(false);
              }
            }
          };
          
          setTimeout(checkSubscription, 1000);
          return;
        }
        
        // Jeśli jest order_id, sprawdź status
        let attempts = 0;
        const maxAttempts = 15; // 15 prób * 1 sekunda = 15 sekund
        
        const checkStatus = async () => {
          attempts++;
          
          try {
            // NAJPIERW sprawdź Subscription (najszybsze i najbardziej wiarygodne)
            const profileUrl = API_BASE ? `${API_BASE}/api/users/profile/` : '/api/users/profile/';
            const profileResponse = await fetch(profileUrl, {
              headers: {
                'Authorization': `Bearer ${userInfo.access}`
              }
            });
            
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();

              if (profileData.user_type) {
                setUserType(profileData.user_type);
              }
              
              // Jeśli subscription jest aktywny, płatność została przetworzona
              if (profileData.subscription && profileData.subscription.is_active) {
                console.log('✓ Subscription is active - payment completed');
                
                // Refresh Redux store with latest user data
                if (userInfo && userInfo.id) {
                  dispatch(getUserDetails(userInfo.id));
                }
                
                setPaymentStatus('COMPLETED');
                setLoading(false);
                return;
              }
            }
            
            // Jeśli subscription nie jest aktywny, sprawdź Payment (dla pewności)
            const statusUrl = API_BASE ? `${API_BASE}/api/payment/status/${orderId}/` : `/api/payment/status/${orderId}/`;
            const paymentResponse = await fetch(statusUrl, {
              headers: {
                'Authorization': `Bearer ${userInfo.access}`
              }
            });
            
            if (paymentResponse.ok) {
              const paymentData = await paymentResponse.json();
              
              if (paymentData.status === 'COMPLETED') {
                console.log('✓ Payment status is COMPLETED');
                
                // Refresh Redux store with latest user data
                if (userInfo && userInfo.id) {
                  dispatch(getUserDetails(userInfo.id));
                }
                
                setPaymentStatus('COMPLETED');
                setLoading(false);
                return;
              }
            }
            
            // Jeśli ani Subscription ani Payment nie są gotowe
            if (attempts < maxAttempts) {
              console.log(`Attempt ${attempts}/${maxAttempts} - waiting...`);
              setTimeout(checkStatus, 1000);
            } else {
              console.error('Max attempts reached - payment verification failed');
              setError('Nie udało się potwierdzić płatności. Jeśli płatność została pobrana, skontaktuj się z nami.');
              setPaymentStatus('FAILED');
              setLoading(false);
            }
          } catch (err) {
            console.error('Błąd sprawdzania statusu:', err);
            
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 1000);
            } else {
              setError('Nie udało się potwierdzić płatności. Jeśli płatność została pobrana, skontaktuj się z nami.');
              setPaymentStatus('FAILED');
              setLoading(false);
            }
          }
        };
        
        // Rozpocznij sprawdzanie natychmiast (bez opóźnienia)
        checkStatus();
        
      } catch (err) {
        console.error('Błąd inicjalizacji:', err);
        setError('Nie udało się potwierdzić płatności');
        setPaymentStatus('ERROR');
        setLoading(false);
      }
    };

    if (userInfo && userInfo.access) {
      checkPaymentStatus();
    } else {
      navigate('/login');
    }
  }, [userInfo, searchParams, API_BASE, navigate]);

  const isEmployer = userType === 'employer';

  const handleStartNow = () => {
    navigate(isEmployer ? '/employer-home' : '/home-screen');
  };

  const handleRetry = () => {
    navigate(isEmployer ? '/employer-app-pricing' : '/app-pricing');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(102, 126, 234, 0.1)'
      }}>
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={6} className="text-center">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <h3 className="text-white">Weryfikacja płatności...</h3>
              <p className="text-white-50">Sprawdzam status Twojej płatności. To może potrwać kilka sekund.</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Jeśli status nie jest COMPLETED, pokaż komunikat
  if (!paymentStatus || (paymentStatus !== 'COMPLETED' && paymentStatus !== 'CANCELED')) {
    const isFailed = paymentStatus === 'FAILED';
    const headerGradient = isFailed 
      ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
      : 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)';
    const iconColor = isFailed ? '#ff6b6b' : '#ffc107';
    const titleColor = isFailed ? '#ff6b6b' : '#ffc107';
    const title = isFailed ? 'Płatność się nie powiodła' : 'Płatność w trakcie przetwarzania';
    const subtitle = isFailed ? 'Spróbuj ponownie' : 'Czekaj na potwierdzenie';
    
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(102, 126, 234, 0.1)',
          padding: '2rem'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card
                  className="border-0 shadow-lg"
                  style={{
                    borderRadius: '30px',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className="text-center py-5"
                    style={{
                      background: headerGradient
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    >
                      <div
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto'
                        }}
                      >
                        <FaExclamationCircle size={70} style={{ color: iconColor }} />
                      </div>
                    </motion.div>
                  </div>

                  <Card.Body className="p-5 text-center">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h1 className="display-4 fw-bold mb-3" style={{ color: titleColor }}>
                        {title}
                      </h1>
                      <h3 className="mb-4 text-muted">
                        {subtitle}
                      </h3>

                      <p className="lead mb-4" style={{ fontSize: '1.2rem', lineHeight: '1.8' }}>
                        {isFailed 
                          ? (error || 'Płatność się nie powiodła.')
                          : 'Twoja płatność jest przetwarzana. Subskrypcja zostanie aktywowana w ciągu kilku sekund.'
                        }
                      </p>

                      <div className="mt-5 d-flex gap-3 flex-column flex-md-row justify-content-center">
                        {isFailed ? (
                          <>
                            <GradientButton
                              onClick={handleRetry}
                              gradient='purple'
                            >
                              Powrót do planów
                            </GradientButton>
                            <button
                              onClick={handleStartNow}
                              style={{
                                padding: '12px 30px',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                borderRadius: '15px',
                                border: 'none',
                                background: 'transparent',
                                color: 'var(--bs-primary)',
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                            >
                              Powrót na stronę główną
                            </button>
                          </>
                        ) : (
                          <>
                            <GradientButton
                              onClick={() => window.location.reload()}
                              gradient='purple'
                            >
                              Odśwież stronę
                            </GradientButton>
                          </>
                        )}
                      </div>
                    </motion.div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Status CANCELED - pokaż komunikat o anulowaniu
  if (paymentStatus === 'CANCELED') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(102, 126, 234, 0.1)',
          padding: '2rem'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card
                  className="border-0 shadow-lg"
                  style={{
                    borderRadius: '30px',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className="text-center py-5"
                    style={{
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    >
                      <div
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto'
                        }}
                      >
                        <FaExclamationCircle size={70} style={{ color: '#ff6b6b' }} />
                      </div>
                    </motion.div>
                  </div>

                  <Card.Body className="p-5 text-center">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h1 className="display-4 fw-bold mb-3" style={{ color: '#ff6b6b' }}>
                        Płatność anulowana
                      </h1>
                      <h3 className="mb-4 text-muted">
                        Transakcja została anulowana
                      </h3>

                      <p className="lead mb-4" style={{ fontSize: '1.2rem', lineHeight: '1.8' }}>
                        {error || 'Płatność została anulowana. Możesz spróbować ponownie.'}
                      </p>

                      <div className="mt-5 d-flex gap-3 flex-column flex-md-row justify-content-center">
                        <GradientButton
                          onClick={handleRetry}
                          gradient='purple'
                        >
                          Powrót do planów
                        </GradientButton>
                        <button
                          onClick={handleStartNow}
                          style={{
                            padding: '12px 30px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            borderRadius: '15px',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--bs-primary)',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                        >
                          Powrót na stronę główną
                        </button>
                      </div>
                    </motion.div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ─── Status COMPLETED — PRACODAWCA ────────────────────────────────────────
  if (isEmployer) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(102, 126, 234, 0.1)',
          padding: '2rem'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card
                  className="border-0 shadow-lg"
                  style={{
                    borderRadius: '30px',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className="text-center py-5"
                    style={{
                      background: 'linear-gradient(135deg, #0d6efd 0%, #0056b3 100%)'
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    >
                      <div
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto'
                        }}
                      >
                        <FaCheckCircle size={70} style={{ color: '#43e97b' }} />
                      </div>
                    </motion.div>
                  </div>

                  <Card.Body className="p-5 text-center">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h1 className="display-4 fw-bold mb-3" style={{ color: '#0d6efd' }}>
                        Plan pracodawcy aktywowany!
                      </h1>
                      <h3 className="mb-4 text-muted">
                        Twoje konto firmowe jest gotowe
                      </h3>

                      <p className="lead mb-4" style={{ fontSize: '1.2rem', lineHeight: '1.8' }}>
                        Płatność została zaksięgowana, a limit pracowników w Twoim panelu HR
                        został zaktualizowany. Możesz teraz dodawać pracowników i zarządzać
                        ich urlopami.
                      </p>

                      <Row className="g-3 mb-4 mt-5 justify-content-center">
                        <Col xs={12} md={5}>
                          <Card
                            className="border-0 h-100"
                            style={{
                              background: 'rgba(13, 110, 253, 0.08)',
                              borderRadius: '15px'
                            }}
                          >
                            <Card.Body className="p-3">
                              <div className="mb-2 text-primary">
                                <FaUsers size={28} />
                              </div>
                              <h6 className="fw-bold mb-1">Panel HR</h6>
                              <small className="text-muted">
                                Dodawaj i zarządzaj pracownikami
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col xs={12} md={5}>
                          <Card
                            className="border-0 h-100"
                            style={{
                              background: 'rgba(13, 110, 253, 0.08)',
                              borderRadius: '15px'
                            }}
                          >
                            <Card.Body className="p-3">
                              <div className="mb-2 text-primary">
                                <FaCalendarCheck size={28} />
                              </div>
                              <h6 className="fw-bold mb-1">Kalendarz urlopowy</h6>
                              <small className="text-muted">
                                Śledź i zatwierdzaj urlopy zespołu
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <div className="mt-5">
                        <GradientButton
                          onClick={handleStartNow}
                          gradient='blue'
                        >
                          Przejdź do strony głównej
                        </GradientButton>
                      </div>
                    </motion.div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ─── Status COMPLETED — PRACOWNIK (domyślny) ─────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(102, 126, 234, 0.1)',
        padding: '2rem'
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} xl={6}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '30px',
                  overflow: 'hidden'
                }}
              >
                <div
                  className="text-center py-5"
                  style={{
                    background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)'
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  >
                    <div
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto'
                      }}
                    >
                      <FaCheckCircle size={70} style={{ color: '#43e97b' }} />
                    </div>
                  </motion.div>
                </div>

                <Card.Body className="p-5 text-center">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <h1 className="display-4 fw-bold mb-3" style={{ color: 'var(--bs-primary)' }}>
                      Płatność zakończona sukcesem!
                    </h1>
                    <h3 className="mb-4 text-muted">
                      Dziękujemy za dołączenie do SIGMA WORK!
                    </h3>

                    <p className="lead mb-4" style={{ fontSize: '1.2rem', lineHeight: '1.8' }}>
                      Twoja subskrypcja została aktywowana. Teraz masz dostęp do wszystkich
                      funkcji premium, które pomogą Ci w zarządzaniu karierą zawodową.
                    </p>

                    <Row className="g-3 mb-4 mt-5">
                      <Col xs={12} md={4}>
                        <Card
                          className="border-0 h-100"
                          style={{
                            background: 'rgba(118, 75, 162, 0.1)',
                            borderRadius: '15px'
                          }}
                        >
                          <Card.Body className="p-3">
                            <div style={{ fontSize: '2rem' }} className="mb-2">📊</div>
                            <h6 className="fw-bold mb-1">Pełna kontrola</h6>
                            <small className="text-muted">
                              Zarządzaj urlopami i zatrudnieniem
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={12} md={4}>
                        <Card
                          className="border-0 h-100"
                          style={{
                            background: 'rgba(118, 75, 162, 0.1)',
                            borderRadius: '15px'
                          }}
                        >
                          <Card.Body className="p-3">
                            <div style={{ fontSize: '2rem' }} className="mb-2">📝</div>
                            <h6 className="fw-bold mb-1">Generator CV</h6>
                            <small className="text-muted">
                              Profesjonalne CV w kilka minut
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={12} md={4}>
                        <Card
                          className="border-0 h-100"
                          style={{
                            background: 'rgba(118, 75, 162, 0.1)',
                            borderRadius: '15px'
                          }}
                        >
                          <Card.Body className="p-3">
                            <div style={{ fontSize: '2rem' }} className="mb-2">💰</div>
                            <h6 className="fw-bold mb-1">Kalkulatory</h6>
                            <small className="text-muted">
                              Wynagrodzenia i świadczenia
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    <div className="mt-5">
                      <GradientButton
                        onClick={handleStartNow}
                        gradient='purple'
                      >
                        Rozpocznij
                      </GradientButton>
                    </div>
                  </motion.div>
                </Card.Body>

                <div className="text-center pb-4">
                  {[...Array(5)].map((_, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      style={{ display: 'inline-block', margin: '0 5px' }}
                    >
                      <FaStar style={{ color: '#ffd700', fontSize: '1.5rem' }} />
                    </motion.span>
                  ))}
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PaymentSuccess;